import React, { useState, useEffect, useRef, useMemo } from 'react';
import Sidebar from './Sidebar';
import ToastContainer from '../ui/Toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import Footer from './Footer';

const PAGE_META = {
  '/telegram':  { title: 'Telegram Bot Demo',  sub: 'How field photos become structured data' },
  '/dashboard': { title: 'Dashboard',          sub: 'All activity at a glance' },
  '/pending':   { title: 'Inventory',          sub: 'Bot-submitted entries awaiting your review' },
  '/clients':   { title: 'Client Master',      sub: 'Manage clients and their stock' },
  '/ledger':    { title: 'Stock Ledger',       sub: 'Real-time sheet balance per client' },
  '/workcards': { title: 'Work Cards',         sub: 'Manage print jobs from work tickets' },
  '/billing':   { title: 'Billing Generator',  sub: 'Generate invoices from completed work cards' },
  '/invoices':  { title: 'Invoices & Billing', sub: 'Track all outgoing invoices' },
};

export default function Layout({ children, actions }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { title, sub } = PAGE_META[pathname] || { title: 'Aishwarya Printers', sub: '' };
  const { doLogout, state, cl } = useApp();

  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const [searchQ, setSearchQ] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (theme === 'light') {
      document.body.classList.add('light-theme');
    } else {
      document.body.classList.remove('light-theme');
    }
    localStorage.setItem('theme', theme);
    window.dispatchEvent(new Event('theme-change'));
  }, [theme]);

  // Close search on outside click
  useEffect(() => {
    function onClickOut(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearch(false);
        setSearchQ('');
      }
    }
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  // Global search results
  const searchResults = useMemo(() => {
    if (!searchQ.trim()) return [];
    const q = searchQ.toLowerCase();
    const results = [];
    // Clients
    state.clients.forEach(c => {
      if ((c.name || '').toLowerCase().includes(q) || (c.gstin || '').toLowerCase().includes(q)) {
        results.push({ type: 'client', icon: 'fa-users', label: c.name, sub: c.gstin || 'No GSTIN', action: () => navigate('/clients') });
      }
    });
    // Invoices
    state.invoices.forEach(inv => {
      const clientName = cl(inv.client_id)?.name || '';
      if ((inv.inv_no || '').toLowerCase().includes(q) || clientName.toLowerCase().includes(q)) {
        results.push({ type: 'invoice', icon: 'fa-receipt', label: `Invoice ${inv.inv_no}`, sub: clientName || 'Unknown Client', action: () => navigate('/invoices') });
      }
    });
    // Work Tickets
    state.workcards.forEach(wc => {
      const clientName = cl(wc.client_id)?.name || '';
      const no = wc.no || '';
      if (no.toLowerCase().includes(q) || clientName.toLowerCase().includes(q) || (wc.job?.job_type || '').toLowerCase().includes(q)) {
        results.push({ type: 'ticket', icon: 'fa-clipboard-list', label: `Ticket #${no}`, sub: clientName || 'Unlinked', action: () => navigate('/workcards') });
      }
    });
    return results.slice(0, 8);
  }, [searchQ, state.clients, state.invoices, state.workcards, cl, navigate]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <div className="app-container">
      <Sidebar />
      
      <main className="main-content">
        {/* ── top-header ── */}
        <header className="top-header">
          <div className="header-search" ref={searchRef} style={{ position: 'relative' }}>
            <i className="fa-solid fa-magnifying-glass"></i>
            <input
              type="text"
              placeholder="Search clients, tickets, invoices…"
              id="global-search"
              value={searchQ}
              onChange={e => { setSearchQ(e.target.value); setShowSearch(true); }}
              onFocus={() => setShowSearch(true)}
              autoComplete="off"
            />
            {searchQ && (
              <button
                onClick={() => { setSearchQ(''); setShowSearch(false); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12, padding: '0 4px' }}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            )}
            {/* Search dropdown */}
            {showSearch && searchQ && (
              <div className="global-search-dropdown">
                {searchResults.length === 0 ? (
                  <div className="search-no-results">No results for "{searchQ}"</div>
                ) : (
                  searchResults.map((r, i) => (
                    <div
                      key={i}
                      className="search-result-item"
                      onClick={() => { r.action(); setShowSearch(false); setSearchQ(''); }}
                    >
                      <i className={`fa-solid ${r.icon}`}></i>
                      <div>
                        <div className="search-result-label">{r.label}</div>
                        <div className="search-result-sub">{r.sub}</div>
                      </div>
                      <span className="search-result-type">{r.type}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <div className="header-actions">
            <div className="theme-toggle" onClick={toggleTheme} title={theme === 'dark' ? "Switch to Light Mode" : "Switch to Dark Mode"}>
              <i className={theme === 'dark' ? 'fa-solid fa-moon' : 'fa-solid fa-sun'} style={{ color: theme === 'dark' ? 'inherit' : '#ff9100' }}></i>
            </div>
            <div className="logout-btn" onClick={doLogout} title="Sign Out">
              <i className="fa-solid fa-arrow-right-from-bracket"></i>
            </div>
          </div>
        </header>

        {/* ── view-pane ── */}
        <div className="view-pane active">
          <div className="view-header-row">
            <div className="view-header">
              <h1>{title}</h1>
              <div className="subtitle">{sub}</div>
            </div>
            {actions && <div className="view-filters">{actions}</div>}
          </div>
          
          {children}
        </div>

        <Footer />
      </main>

      <ToastContainer />
    </div>
  );
}
